import { LoginService } from "./service";
import { getCookie, setCookie } from "../../utils/cookies";

let inFlightTenantsPromise = null;

export const auth = {
  state: {
    isAuthenticated: !!getCookie("adminToken"),
    user: JSON.parse(getCookie("adminAuth") || "null"),
    profile: null,
    error: null,
    isLoading: false,
    accountLocked: false,
    tenants: [],
  },
  reducers: {
    setLoading(state, payload) {
      return { ...state, isLoading: payload };
    },
    setError(state, payload) {
      return { ...state, error: payload };
    },
    setAuth(state, payload) {
      return { ...state, isAuthenticated: true, user: payload, error: null };
    },
    setProfile(state, payload) {
      return { ...state, profile: payload };
    },
    setAccountLocked(state, payload) {
      return { ...state, accountLocked: payload };
    },
    setTenants(state, payload) {
      return { ...state, tenants: payload };
    },
    logoutState(state) {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        profile: null,
        error: null,
        accountLocked: false,
        tenants: [],
      };
    },
  },
  effects: (dispatch) => ({
    async loginAsync(payload, rootState) {
      dispatch.auth.setLoading(true);
      dispatch.auth.setError(null);
      try {
        const data = await LoginService.login(payload.email, payload.password);
        const token = data?.data?.token || data?.token;
        const tempToken = data?.data?.tempToken || data?.tempToken;

        if (tempToken) {
          // MFA is required
          return { success: true, requiresMFA: true, tempToken, email: payload.email };
        }

        if (token) {
          const apiUserData = data?.data?.user || data?.user || {};
          const roleName = apiUserData.role?.name || apiUserData.role || "Admin";
          const isSuperAdmin = String(roleName).toUpperCase().replace(/[_\s-]+/g, "") === "SUPERADMIN";
          const tenantId = isSuperAdmin
            ? null
            : (data?.data?.tenantId ||
               data?.tenantId ||
               apiUserData.tenantId ||
               null);
          const refreshToken = data?.data?.refreshToken || data?.refreshToken;

          const userData = {
            email: payload.email,
            role: apiUserData.role || "Admin",
            ...apiUserData,
            tenantId,
          };

          setCookie("adminToken", token);
          setCookie("adminAuth", JSON.stringify(userData));
          localStorage.setItem("adminAuth", JSON.stringify(userData));
          if (isSuperAdmin) {
            setCookie("adminGlobalToken", token);
          }
          if (refreshToken) {
            setCookie("adminRefreshToken", refreshToken);
            if (isSuperAdmin) {
              setCookie("adminGlobalRefreshToken", refreshToken);
            }
          }
          dispatch.auth.setAuth(userData);
          return { success: true };
        }

        throw new Error("No token received from the server");
      } catch (error) {
        const status = error?.response?.status;
        const data = error?.response?.data;
        const isLocked = status === 423 || data?.accountLocked === true;
        if (isLocked) {
          dispatch.auth.setAccountLocked(true);
          dispatch.auth.setError("Your account has been locked due to multiple failed login attempts. Please contact your administrator.");
          return { success: false, accountLocked: true };
        }
        const message = data?.message || error.message || "Login failed";
        dispatch.auth.setError(message);
        return { success: false, message: message };
      } finally {
        dispatch.auth.setLoading(false);
      }
    },
    async verifyMfaAsync(payload, rootState) {
      dispatch.auth.setLoading(true);
      dispatch.auth.setError(null);
      try {
        const data = await LoginService.verifyMfa(payload.tempToken, payload.mfaCode, payload.rememberDevice);
        const token = data?.data?.token || data?.token;
        
        if (token) {
          const apiUserData = data?.data?.user || data?.user || {};
          const roleName = apiUserData.role?.name || apiUserData.role || "Admin";
          const isSuperAdmin = String(roleName).toUpperCase().replace(/[_\s-]+/g, "") === "SUPERADMIN";
          const tenantId = isSuperAdmin
            ? null
            : (data?.data?.tenantId || data?.tenantId || apiUserData.tenantId || null);
          const refreshToken = data?.data?.refreshToken || data?.refreshToken;

          const userData = {
            email: payload.email,
            role: apiUserData.role || "Admin",
            ...apiUserData,
            tenantId,
          };

          setCookie("adminToken", token);
          setCookie("adminAuth", JSON.stringify(userData));
          localStorage.setItem("adminAuth", JSON.stringify(userData));
          if (isSuperAdmin) {
            setCookie("adminGlobalToken", token);
          }
          if (refreshToken) {
            setCookie("adminRefreshToken", refreshToken);
            if (isSuperAdmin) {
              setCookie("adminGlobalRefreshToken", refreshToken);
            }
          }
          dispatch.auth.setAuth(userData);
          return { success: true };
        }
        throw new Error("No token received from verification");
      } catch (error) {
        const message = error?.response?.data?.message || error.message || "MFA Verification failed";
        dispatch.auth.setError(message);
        return { success: false, message };
      } finally {
        dispatch.auth.setLoading(false);
      }
    },
    async logoutAsync() {
      await LoginService.logout();
      dispatch.auth.logoutState();
    },
    async fetchTenantsAsync(force = false) {
      if (!force && inFlightTenantsPromise) {
        return inFlightTenantsPromise;
      }
      inFlightTenantsPromise = (async () => {
        try {
          const data = await LoginService.getTenants();
          const list = data?.data || data || [];
          dispatch.auth.setTenants(Array.isArray(list) ? list : []);
          return { success: true, tenants: Array.isArray(list) ? list : [] };
        } catch (error) {
          console.error("FetchTenants error:", error);
          return { success: false, message: error.message, tenants: [] };
        } finally {
          inFlightTenantsPromise = null;
        }
      })();
      return inFlightTenantsPromise;
    },
    /**
     * switchTenantAsync — Calls the backend switch-tenant API to get a
     * tenant-scoped token. The global token is preserved separately so
     * we can restore it later without another API call.
     */
    async switchTenantAsync(payloadTenantId) {
      // payload could be an object: { payloadTenantId, stringTenantId }
      const isObj = typeof payloadTenantId === 'object' && payloadTenantId !== null;
      const propertyUuid = isObj ? payloadTenantId.payloadTenantId : payloadTenantId;
      const stringTenantId = isObj ? payloadTenantId.stringTenantId : null;

      dispatch.auth.setLoading(true);
      dispatch.auth.setError(null);
      try {
        // Send the UUID to the backend switch endpoint
        const data = await LoginService.switchTenant(propertyUuid);
        const token = data?.data?.token || data?.token;
        const refreshToken = data?.data?.refreshToken || data?.refreshToken;
        const apiTenantId = data?.data?.tenantId || data?.tenantId;
        const apiUserData = data?.data?.user || data?.user || {};

        // Priority: API tenantId > numeric stringTenantId > UUID fallback
        const resolvedTenantId = apiTenantId || stringTenantId || propertyUuid;

        if (token) {
          // Store tenant-scoped token as the active token
          setCookie("adminToken", token);
          if (refreshToken) {
            setCookie("adminRefreshToken", refreshToken);
          }
          const currentUser = JSON.parse(getCookie("adminAuth") || localStorage.getItem("adminAuth") || "{}");
          const updatedUser = { ...currentUser, ...apiUserData, tenantId: resolvedTenantId, hotelId: propertyUuid };
          setCookie("adminAuth", JSON.stringify(updatedUser));
          localStorage.setItem("adminAuth", JSON.stringify(updatedUser));
          dispatch.auth.setAuth(updatedUser);
        } else {
          // No new token — just update tenant ID locally
          const currentUser = JSON.parse(getCookie("adminAuth") || localStorage.getItem("adminAuth") || "{}");
          const updatedUser = { ...currentUser, tenantId: resolvedTenantId, hotelId: propertyUuid };
          setCookie("adminAuth", JSON.stringify(updatedUser));
          localStorage.setItem("adminAuth", JSON.stringify(updatedUser));
          dispatch.auth.setAuth(updatedUser);
        }
        return { success: true };
      } catch (error) {
        const message = error?.response?.data?.message || error.message || "Failed to switch tenant";
        dispatch.auth.setError(message);
        return { success: false, message };
      } finally {
        dispatch.auth.setLoading(false);
      }
    },
    /**
     * restoreGlobalSession — Frontend-only: restores the Global Token that
     * was saved at login time. No API call is made. The tenant-scoped token
     * is discarded and the user returns to the "All Properties" context.
     */
    restoreGlobalSession() {
      const globalToken = getCookie("adminGlobalToken");
      const globalRefreshToken = getCookie("adminGlobalRefreshToken");

      if (!globalToken) {
        console.warn("[restoreGlobalSession] No global token found — cannot restore.");
        return { success: false, message: "No global session to restore" };
      }

      // Swap the active token back to the global one
      setCookie("adminToken", globalToken);
      if (globalRefreshToken) {
        setCookie("adminRefreshToken", globalRefreshToken);
      }

      // Clear tenant context from the stored user data
      const currentUser = JSON.parse(getCookie("adminAuth") || localStorage.getItem("adminAuth") || "{}");
      const updatedUser = { ...currentUser, tenantId: null };
      setCookie("adminAuth", JSON.stringify(updatedUser));
      localStorage.setItem("adminAuth", JSON.stringify(updatedUser));
      dispatch.auth.setAuth(updatedUser);

      return { success: true };
    },
    async fetchMeAsync() {
      try {
        const userData = await LoginService.getCurrentUser();
        if (userData) {
          dispatch.auth.setProfile(userData);

          // Sync the fresh profile data into the user state and cookies/localStorage
          const currentUser = JSON.parse(getCookie("adminAuth") || localStorage.getItem("adminAuth") || "{}");
          const roleName = userData.role?.name || userData.role || currentUser.role?.name || currentUser.role;
          const isSuperAdmin = String(roleName).toUpperCase().replace(/[_\s-]+/g, "") === "SUPERADMIN";
          const tenantId = isSuperAdmin 
            ? (currentUser.tenantId || null) 
            : (currentUser.tenantId || userData.tenantId || null);
          const updatedUser = { 
            ...currentUser, 
            ...userData,
            tenantId
          };
          setCookie("adminAuth", JSON.stringify(updatedUser));
          localStorage.setItem("adminAuth", JSON.stringify(updatedUser));
          dispatch.auth.setAuth(updatedUser);

          return { success: true, profile: userData };
        }
      } catch (error) {
        console.error("FetchMe error:", error);
        return { success: false, message: error.message };
      }
    },
    async forgotPasswordAsync(payload) {
      dispatch.auth.setLoading(true);
      dispatch.auth.setError(null);
      try {
        await LoginService.forgotPassword(payload.email);
        return { success: true };
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error.message ||
          "Failed to send OTP";
        dispatch.auth.setError(message);
        return { success: false, message };
      } finally {
        dispatch.auth.setLoading(false);
      }
    },
    async verifyOtpAsync(payload) {
      dispatch.auth.setLoading(true);
      dispatch.auth.setError(null);
      try {
        const data = await LoginService.verifyOtp(payload.email, payload.otp);
        // Extract resetToken from data.data.resetToken based on API response format
        const token = data?.data?.resetToken || data?.resetToken || data?.data?.token || data?.token;
        return { success: true, token };
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error.message ||
          "Invalid or expired OTP";
        dispatch.auth.setError(message);
        return { success: false, message };
      } finally {
        dispatch.auth.setLoading(false);
      }
    },
    async resetPasswordAsync(payload) {
      dispatch.auth.setLoading(true);
      dispatch.auth.setError(null);
      try {
        await LoginService.resetPassword(payload.token, payload.password);
        return { success: true };
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error.message ||
          "Failed to reset password";
        dispatch.auth.setError(message);
        return { success: false, message };
      } finally {
        dispatch.auth.setLoading(false);
      }
    },
    async changePasswordAsync({ oldPassword, newPassword }) {
      dispatch.auth.setLoading(true);
      dispatch.auth.setError(null);
      try {
        await LoginService.changePassword(oldPassword, newPassword);
        return { success: true };
      } catch (error) {
        dispatch.auth.setError(error);
        return { success: false, message: error };
      } finally {
        dispatch.auth.setLoading(false);
      }
    },
  }),
};
